import { type RefObject, useLayoutEffect, useState } from "react";
import { BANNER_COLS } from "./banner";

/**
 * How many characters fit across the banner pane, right now.
 *
 * The banner is drawn in characters, so "as wide as the terminal" is a question
 * about the font, not about CSS: the box can only end where a character ends.
 * This measures the advance width of the glyphs the banner actually draws and
 * divides the pane's content box by it.
 *
 * **Measured rather than assumed.** `ch` would be the CSS unit for this, but it
 * is defined as the advance of `0` — the banner also draws `─`, and a stack
 * where those two differ would put the rules and the content rows at different
 * widths. Taking the wider of the two keeps every line inside the pane; if they
 * disagree at all the box is already broken, and this at least fails visibly
 * rather than by overflowing into a hidden scroll.
 *
 * The probe is appended to the `<pre>` rather than the pane so it inherits the
 * element whose text is being measured — the pane and the banner do not
 * necessarily carry the same font-family, and that is deliberate (see
 * `ubuntu.css`).
 */

/** Enough characters that sub-pixel advances average out to something exact. */
const PROBE_LENGTH = 100;

export function useBannerCols(
  paneRef: RefObject<HTMLElement | null>,
  preRef: RefObject<HTMLElement | null>,
): number {
  // 80 until measured: the width a terminal has always been, and what the
  // banner falls back to if there is no layout to measure.
  const [cols, setCols] = useState(BANNER_COLS);

  useLayoutEffect(() => {
    const pane = paneRef.current;
    const pre = preRef.current;
    if (!pane || !pre) return;

    const measure = () => {
      const probe = document.createElement("span");
      probe.style.cssText = "position:absolute;visibility:hidden;white-space:pre;";

      let advance = 0;
      for (const glyph of ["0", "─"]) {
        probe.textContent = glyph.repeat(PROBE_LENGTH);
        pre.appendChild(probe);
        advance = Math.max(advance, probe.getBoundingClientRect().width / PROBE_LENGTH);
      }
      probe.remove();

      const style = getComputedStyle(pane);
      const inner =
        pane.clientWidth -
        Number.parseFloat(style.paddingLeft) -
        Number.parseFloat(style.paddingRight);

      // Floor, never round: half a column of overflow is a horizontal scrollbar
      // and a right-hand border the viewport cuts off.
      if (advance > 0 && inner > 0) setCols(Math.floor(inner / advance));
    };

    measure();

    // No observer is not a failure: the banner keeps the width it was measured
    // at, or BANNER_COLS where there was nothing to measure. jsdom has no
    // ResizeObserver, and a terminal that throws on render because it could not
    // arrange to be told about resizes would be a poor trade.
    if (typeof ResizeObserver === "undefined") return;

    // Resize alone is not enough — the pane also changes width when the theme
    // or the setup changes, neither of which resizes the window.
    const observer = new ResizeObserver(measure);
    observer.observe(pane);
    return () => observer.disconnect();
  }, [paneRef, preRef]);

  return cols;
}
